import React, { JSX } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LocalStorageUser } from "../types/localStorage.d";
import { RoleRouteProps } from "../types/routes.d";

// Layout components
import DashboardLayout from "../components/layout/DashboardLayout";
import CompanyLayout from "../components/layout/CompanyLayout"; // Import CompanyLayout
import JobSeekerLayout from "../components/layout/JobSeekerLayout";

// General pages
import Landing from "../pages/Landing/Landing";
import Home from "../pages/General/Home";
import HomeNew from "../pages/HomeNew";
import HomeNewV2 from "../pages/HomeNewV2";
import Jobs from "../pages/General/Jobs";
import JobsList from "../pages/General/JobsList";
import About from "../pages/General/About";
import Contact from "../pages/General/Contact";
import Services from "../pages/General/Services";
import JobSeekerServices from "../pages/General/JobSeekerServices";
import CompanyServices from "../pages/General/CompanyServices";
import Unauthorized from "../pages/General/Unauthorized";
import Privacy from "../pages/General/Privacy";
import Terms from "../pages/General/Terms";
import Cookies from "../pages/General/Cookies";
import FAQ from "../pages/General/FAQ";
import CancellationRefunds from "../pages/General/CancellationRefunds";

// Auth pages
import Login from "../pages/Auth/Login";
import Register from "../pages/Auth/Register";
import OTPVerify from "../pages/General/OTPVerify";
import PasswordSetup from "../pages/General/PasswordSetup";
import ForgotPassword from "../pages/Auth/ForgotPassword";

// User pages
import UserDashboard from "../pages/User/Dashboard";
import DashboardForm from "../pages/User/DashboardForm";
import UserJobs from "../pages/User/Jobs";
import UserApply from "../pages/User/Apply";
import UserApplications from "../pages/User/UserApplications";
import ApplyMultiStep from "../pages/User/ApplyMultiStep";
import UserProfile from "../pages/User/Profile";
import UserProfileForm from "../pages/User/UserProfileForm";
import UserProfileSummary from "../pages/User/UserProfileSummary";
import Payment from "../pages/User/Payment";
import PaymentSuccess from "../pages/User/PaymentSuccess";

// Company pages
import CompanyDashboard from "../pages/Company/CompanyDashboard";
import JobList from "../pages/Company/JobList";
import JobForm from "../pages/Company/JobForm";
import JobApplications from "../pages/Company/JobApplications";
import RegisterCompany from "../pages/Company/RegisterCompany";
import CompanyProfile from "../pages/Company/CompanyProfile";
// import CompanyProfileForm from '../pages/Company/CompanyProfileForm';
import CompanyProfileSummary from "../pages/Company/CompanyProfileSummary";
import PublicCompanyProfile from "../pages/Company/PublicCompanyProfile";
import PublicUserProfile from "../pages/User/PublicUserProfile";
import TestComponent from "../pages/Company/TestComponent";
import PublicJobPost from "../pages/Job/PublicJobPost";
import Applications from "../pages/Job/Applications";

// Agent pages
import AgentDashboard from "../pages/Agent/AgentDashboard";
import PendingCompanies from "../pages/Agent/PendingCompanies";
import AllCompanies from "../pages/Agent/AllCompanies"; // Import AllCompanies
import ManageApplications from "../pages/Agent/ManageApplications";
import Reports from "../pages/Agent/Reports";
import AgentOnboarding from "../pages/Agent/AgentOnboarding";
// import AgentProfileForm from '../pages/Agent/AgentProfileForm';
import AgentProfileSummary from "../pages/Agent/AgentProfileSummary";
import BroadcastEmail from '../pages/Agent/BroadcastEmail';
import AgentInquiries from '../pages/Agent/AgentInquires';
import AgentAllPosts from "../pages/Agent/AllPosts";

// Admin pages
import AdminDashboard from "../pages/Admin/AdminDashboard";
import AdminOnboarding from "../pages/Admin/AdminOnboarding";
import AdminPendingCompanies from "../pages/Admin/AdminPendingCompanies";
import AdminAllCompanies from "../pages/Admin/AdminAllCompanies";
import AdminAllUsers from "../pages/Admin/AdminAllUsers"; // Import AdminAllUsers
import AdminAllPosts from "../pages/Admin/AllPosts";

// Agent pages
import AgentAllUsers from "../pages/Agent/AgentAllUsers"; // Import AgentAllUsers
import AgentExternalCandidates from "../pages/Agent/AgentExternalCandidates";

// Admin pages
import AdminExternalCandidates from "../pages/Admin/AdminExternalCandidates";

// Route components
import ProtectedRoute from "./ProtectedRoute";
import RoleRoute from "./RoleRoute";
import CompanyRegistrationCheck from "../components/CompanyRegistrationCheck";
import AgentLayout from "../components/AgentLayout";
import RoleBasedRedirect from "../components/RoleBasedRedirect";
import { CompanyProvider } from "../context/CompanyContext";
import { JobSeekerProvider } from "../context/JobSeekerContext";
import { AgentProvider } from "../context/AgentContext"; // Import AgentProvider
import UserProfileEdit from "../pages/User/UserProfileEdit";
import { AdminProvider } from "../context/AdminContext";
import AdminLayout from "../components/AdminLayout";
const AppRoutes = (): JSX.Element => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomeNewV2 />} />
      <Route path="/home-v1" element={<HomeNew />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/about" element={<About />} />
      {/* Removed Jobs pages; redirect old paths to Services */}
      <Route path="/jobs" element={<Navigate to="/services" replace />} />
      {/* <Route path="/jobs/:id" element={<Navigate to="/services" replace />} /> */}
      <Route path="/contact" element={<Contact />} />
      <Route path="/services" element={<Services />} />
      <Route path="/services/job-seekers" element={<JobSeekerServices />} />
      <Route path="/services/companies" element={<CompanyServices />} />
      <Route path="/unauthorized" element={<Unauthorized />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/cancellation-refunds" element={<CancellationRefunds />} />

      {/* Auth routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/password-setup" element={<PasswordSetup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />

      {/* Role-based redirect for authenticated users */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <RoleBasedRedirect />
          </ProtectedRoute>
        }
      />

      {/* User routes */}
      <Route
        path="/user/*"
        element={
          <ProtectedRoute>
            <RoleRoute roles={["Jobseeker", "Intern"] as LocalStorageUser["role"][]}>
              <JobSeekerProvider>
                <JobSeekerLayout>
                  <Routes>
                    <Route path="dashboard" element={<UserDashboard />} />
                    <Route path="dashboard/form" element={<DashboardForm />} />
                    <Route path="jobs" element={<UserJobs />} />
                    <Route path="apply" element={<UserApply />} />
                    <Route path="apply/:jobId" element={<ApplyMultiStep />} />
                    <Route path="status" element={<UserApplications />} />
                    <Route path="profile" element={<UserProfile />} />
                    <Route path="profile/edit" element={<UserProfileEdit />} />
                    <Route
                      path="profile/complete"
                      element={<UserProfileForm />}
                    />
                    <Route
                      path="profile/summary"
                      element={<UserProfileSummary />}
                    />
                    <Route path="payment" element={<Payment />} />
                    <Route
                      path="payment/success"
                      element={<PaymentSuccess />}
                    />
                    <Route
                      path="post/:postId"
                      element={<Jobs />}
                    />
                    <Route
                      path="companyProfile/:companyId"
                      element={
                          <PublicCompanyProfile />
                      }
                    />
                  </Routes>
                </JobSeekerLayout>
              </JobSeekerProvider>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Company registration route (without CompanyLayout) */}
      <Route
        path="/company/register"
        element={
          <ProtectedRoute>
            <RoleRoute roles={["Company"] as LocalStorageUser["role"][]}>
              <CompanyProvider>
                <DashboardLayout
                  companyDetails={{
                    companyStatus: "NotRegistered",
                    jobPostCount: 0,
                  }}
                >
                  <RegisterCompany />
                </DashboardLayout>
              </CompanyProvider>
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      {/* Company routes (with CompanyLayout) */}
      <Route
        path="/company/*"
        element={
          <ProtectedRoute>
            <RoleRoute roles={["Company"] as LocalStorageUser["role"][]}>
              <CompanyProvider>
                <CompanyLayout>
                  {" "}
                  {/* Use CompanyLayout here */}
                  <Routes>
                    <Route path="" element={<CompanyDashboard />} />
                    <Route path="dashboard" element={<CompanyDashboard />} />
                    <Route path="jobs" element={<JobList />} />
                    <Route path="jobs/create" element={<JobForm />} />
                    <Route path="jobs/:postId" element={<Jobs />} />
                    <Route path="jobs/:postId/edit" element={<JobForm />} />
                    <Route path="applications" element={<JobApplications />} />
                    <Route path="profile" element={<CompanyProfile />} />
                    <Route path="profile/edit" element={<CompanyProfile />} />
                    {/* <Route path="profile/complete" element={<CompanyProfileForm />} /> */}
                    <Route
                      path="profile/summary"
                      element={<CompanyProfileSummary />}
                    />
                    <Route path="users" element={<TestComponent />} />
                    <Route
                      path="user/:userId"
                      element={
                          <PublicUserProfile />
                      }
                    />
                    <Route
                      path="post/:postId"
                      element={<Jobs />}
                    />
                  </Routes>
                </CompanyLayout>
              </CompanyProvider>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Agent routes */}
      <Route
        path="/agent/*"
        element={
          <ProtectedRoute>
            <RoleRoute roles={["Agent"] as LocalStorageUser["role"][]}>
              <AgentProvider>
                <AgentLayout>
                  <Routes>
                    <Route path="" element={<AgentDashboard />} />
                    <Route path="dashboard" element={<AgentDashboard />} />
                    <Route path="onboarding" element={<AgentOnboarding />} />
                    <Route path="pending" element={<PendingCompanies />} />
                    <Route path="companies" element={<AllCompanies />} />
                    <Route path="users" element={<AgentAllUsers />} /> {/* New route for AgentAllUsers */}
                    <Route path="external-candidates" element={<AgentExternalCandidates />} />
                    <Route path="posts" element={<AgentAllPosts />} />
                    <Route path="manage" element={<ManageApplications />} />
                    <Route path="reports" element={<Reports />} />
                    <Route path="profile" element={<AgentProfileSummary />} />
                    {/* <Route path="profile/edit" element={<AgentProfileForm />} />  TODO check requirement */}
                    {/* <Route path="profile/complete" element={<AgentProfileForm />} /> TODO check requirement */}
                     <Route path='BroadcastEmail' element={<BroadcastEmail/>}/>
                      <Route path='Inquiries' element={<AgentInquiries/>}/>
                    <Route
                      path="profile/summary"
                      element={<AgentProfileSummary />}
                    />
                    <Route
                      path="companyProfile/:companyId"
                      element={
                          <PublicCompanyProfile />
                      }
                    />
                    <Route
                      path="user/:userId"
                      element={
                          <PublicUserProfile />
                      }
                    />
                    <Route
                      path="post/:postId"
                      element={<Jobs />}
                    />
                    <Route
                        path="post/:postId/applications"
                        element={<Applications />}
                    />
                  </Routes>
                </AgentLayout>
              </AgentProvider>
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      {/* Admin routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute>
            <RoleRoute roles={["Admin"] as LocalStorageUser["role"][]}>
              <AdminProvider>
                <AdminLayout>
                  <Routes>
                    <Route path="" element={<AdminDashboard />} />
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="onboarding" element={<AdminOnboarding />} />
                    <Route path="pending" element={<AdminPendingCompanies />} />
                    <Route path="companies" element={<AdminAllCompanies />} />
                    <Route path="users" element={<AdminAllUsers />} /> {/* New route for AdminAllUsers */}
                    <Route path="external-candidates" element={<AdminExternalCandidates />} />
                    <Route path="posts" element={<AdminAllPosts />} />
                    <Route path='Inquiries' element={<AgentInquiries/>}/>
                    {/* <Route path="manage" element={<ManageApplications />} /> */}
                    {/* <Route path="reports" element={<Reports />} /> */}
                    {/* <Route path="profile" element={<AdminProfileSummary />} /> */}
                    {/* <Route path="profile/edit" element={<AdminProfileForm />} />  TODO check requirement */}
                    {/* <Route path="profile/complete" element={<AdminProfileForm />} /> TODO check requirement */}
                    {/* <Route
                      path="profile/summary"
                      element={<AdminProfileSummary />}
                    /> */}
                    <Route
                      path="companyProfile/:companyId"
                      element={
                          <PublicCompanyProfile />
                      }
                    />
                    <Route
                      path="user/:userId"
                      element={
                          <PublicUserProfile />
                      }
                    />
                    <Route
                      path="post/:postId"
                      element={<Jobs />}
                    />
                    <Route
                        path="post/:postId/applications"
                        element={<Applications />}
                    />
                  </Routes>
                </AdminLayout>
              </AdminProvider>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
